import jsPDF from 'jspdf';

export interface PDFOptions {
  title?: string;
  content: string;
  filename: string;
}

export class PDFExporter {
  private doc: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number;
  private contentWidth: number;
  private currentY: number;
  private lineHeight: number;

  constructor() {
    // Create PDF with 8.5x11 inch dimensions (letter size)
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter'
    });

    // Set page dimensions and margins
    this.pageWidth = 8.5;
    this.pageHeight = 11;
    this.margin = 1; // 1 inch margins on all sides
    this.contentWidth = this.pageWidth - (2 * this.margin);
    this.currentY = this.margin;
    this.lineHeight = 0.2; // 0.2 inches between lines

    // Set Arial font (or similar sans-serif)
    this.doc.setFont('helvetica', 'normal');
  }

  public addTitle(title: string, companyName?: string): void {
    // Set title font: bold, size 18
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(18);
    
    // Calculate center position for title
    const titleWidth = this.doc.getTextWidth(title);
    const centerX = (this.pageWidth - titleWidth) / 2;
    
    // Add the title centered at the top of the page
    this.doc.text(title, centerX, this.currentY);
    
    // Move cursor down after title with extra spacing (3 lines down)
    this.currentY += 0.4; // 2 lines of spacing after title for company name
    
    // Add company name if provided (centered, bold, 2 lines below header)
    if (companyName) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.setFontSize(12);
      
      const companyWidth = this.doc.getTextWidth(companyName);
      const companyCenterX = (this.pageWidth - companyWidth) / 2;
      
      this.doc.text(companyName, companyCenterX, this.currentY);
      this.currentY += 0.6; // Add spacing after company name to maintain section spacing
    } else {
      // If no company name, maintain original spacing for sections
      this.currentY += 0.4;
    }
    
    // Reset font to normal for body text
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(12);
  }

  public addSection(sectionTitle: string, content: string): void {
    // Check if we need a new page
    if (this.currentY > this.pageHeight - this.margin - 1) {
      this.addPage();
    }

    // Add section title (bold)
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(sectionTitle, this.margin, this.currentY);
    this.currentY += this.lineHeight;

    // Add extra carriage return below section header
    this.currentY += this.lineHeight;

    // Reset to normal font for content
    this.doc.setFont('helvetica', 'normal');

    // Split content into lines that fit within the margins
    const lines = this.splitTextToLines(content);
    
    for (const line of lines) {
      // Check if we need a new page
      if (this.currentY > this.pageHeight - this.margin - 0.3) {
        this.addPage();
      }
      
      this.doc.text(line, this.margin, this.currentY);
      this.currentY += this.lineHeight;
    }

    // Add 2 line breaks after section
    this.currentY += 0.4;
  }

  public addText(text: string): void {
    const lines = this.splitTextToLines(text);
    
    for (const line of lines) {
      // Check if we need a new page
      if (this.currentY > this.pageHeight - this.margin - 0.3) {
        this.addPage();
      }
      
      this.doc.text(line, this.margin, this.currentY);
      this.currentY += this.lineHeight;
    }
  }

  private splitTextToLines(text: string): string[] {
    const lines: string[] = [];
    const paragraphs = text.split('\n');
    
    for (const paragraph of paragraphs) {
      if (paragraph.trim() === '') {
        lines.push('');
        continue;
      }
      
      // Split long lines to fit within margins
      const words = paragraph.split(' ');
      let currentLine = '';
      
      for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const textWidth = this.doc.getTextWidth(testLine);
        
        if (textWidth > this.contentWidth) {
          if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            // Single word is too long, force it on its own line
            lines.push(word);
          }
        } else {
          currentLine = testLine;
        }
      }
      
      if (currentLine) {
        lines.push(currentLine);
      }
    }
    
    return lines;
  }

  private addPage(): void {
    this.doc.addPage();
    this.currentY = this.margin;
  }

  public save(filename: string): void {
    this.doc.save(filename);
  }

  public getBlob(): Blob {
    return this.doc.output('blob');
  }
}

// Utility function to create and download a PDF
export const generateAndDownloadPDF = (options: PDFOptions): void => {
  const pdfExporter = new PDFExporter();
  
  // Add title if provided
  if (options.title) {
    pdfExporter.addTitle(options.title);
  }
  
  // Add main content
  pdfExporter.addText(options.content);
  
  // Download the PDF
  pdfExporter.save(options.filename);
};

// More advanced function for structured content
export const generateStructuredPDF = (
  title: string,
  sections: Array<{ title: string; content: string }>,
  filename: string,
  companyName?: string
): void => {
  const pdfExporter = new PDFExporter();
  
  // Add main title with optional company name
  pdfExporter.addTitle(title, companyName);
  
  // Add each section
  for (const section of sections) {
    pdfExporter.addSection(section.title, section.content);
  }
  
  // Download the PDF
  pdfExporter.save(filename);
};